var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/* eslint-disable max-lines */
import { Prisma } from '@workalizer/db';
import { ActivityAlertTypeEnum, AnalyticPeriodsEnum, ApplicationEnum, ChatRoomTypeEnum } from '@workalizer/shared';
import { diffComparisonType, diffDeltaUnit } from '@workalizer/shared/types';
import { addDays, addMonths, addWeeks, differenceInCalendarDays, differenceInDays, differenceInMilliseconds, differenceInMonths, differenceInWeeks, eachDayOfInterval, eachHourOfInterval, eachMonthOfInterval, endOfDay, endOfMonth, endOfWeek, format, formatISO, isSameDay, isSameMonth, parseISO, startOfDay, startOfHour, startOfMonth, startOfWeek, subDays, subMilliseconds } from 'date-fns';
import { format as formatTz, formatInTimeZone, fromZonedTime } from 'date-fns-tz';
export var DISABLED_USERS = [
    {
        profileId: '110353359459171528925',
        email: 'finance@magneticone.com'
    },
    {
        profileId: '105250506097979753968',
        email: 'none'
    },
    {
        profileId: '117136974956284698495',
        email: 'tech@magneticone.com'
    },
    {
        profileId: '114082650375366865706',
        email: 'manager@magneticone.com'
    }
];
export var TIMEZONE_FOR_QUERY_UTC = '+00:00';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export var groupBy = function (arrayToGroup, property) {
    return arrayToGroup.reduce(function (result, item) {
        var key = item[property];
        if (!result[key]) {
            result[key] = [];
        }
        result[key].push(item);
        return result;
    }, {});
};
export var getWeekPeriods = function (dateFrom, dateTo) {
    var startWeekDate = startOfWeek(dateFrom, { weekStartsOn: 1 });
    var weeksDifferent = differenceInWeeks(dateTo, startWeekDate);
    var dataWeekPeriods = Array.from({ length: weeksDifferent + 1 }, function (_, i) {
        var dateFrom = addWeeks(startWeekDate, i);
        var dateTo = endOfWeek(dateFrom, { weekStartsOn: 1 });
        return {
            dateFrom: dateFrom,
            dateTo: dateTo,
            period: "".concat(format(dateFrom, 'dd MMM'), " - ").concat(format(dateTo, 'dd MMM'))
        };
    });
    return dataWeekPeriods;
};
/**
 * Aligns a date range for avg-meetings-per-week: short custom periods extend
 * backward to at least one week ending at dateTo; longer periods align from
 * the Monday of the week containing dateFrom.
 */
export var resolveAvgMeetingsPerWeekBounds = function (dateFrom, dateTo) {
    var alignedDateTo = dateTo;
    var periodDays = differenceInCalendarDays(dateTo, dateFrom) + 1;
    if (periodDays < 7) {
        return {
            alignedDateFrom: startOfDay(subDays(dateTo, 7)),
            alignedDateTo: alignedDateTo,
            weekCount: 1
        };
    }
    var alignedDateFrom = startOfWeek(dateFrom, { weekStartsOn: 1 });
    var weekCount = Math.max(1, getWeekPeriods(alignedDateFrom, alignedDateTo).length);
    return {
        alignedDateFrom: alignedDateFrom,
        alignedDateTo: alignedDateTo,
        weekCount: weekCount
    };
};
export var getMonthPeriods = function (dateFrom, dateTo) {
    var months = eachMonthOfInterval({ start: dateFrom, end: dateTo });
    return months.map(function (monthStart) {
        var monthEnd = endOfMonth(monthStart);
        return {
            dateFrom: startOfMonth(monthStart),
            dateTo: endOfDay(monthEnd),
            period: formatPeriod(AnalyticPeriodsEnum.MONTH, monthStart, monthEnd)
        };
    });
};
export function buildPeriods(from, to, interval) {
    switch (interval) {
        case AnalyticPeriodsEnum.DAY:
            return eachDayOfInterval({ start: from, end: to }).map(function (d) { return ({
                key: formatISO(d, { representation: 'date' }),
                from: d,
                to: d
            }); });
        case AnalyticPeriodsEnum.WEEK:
            return getWeekPeriods(from, to).map(function (w) { return ({
                key: formatPeriod(AnalyticPeriodsEnum.WEEK, w.dateFrom, w.dateTo),
                from: w.dateFrom,
                to: w.dateTo
            }); });
        case AnalyticPeriodsEnum.MONTH:
            return eachMonthOfInterval({ start: from, end: to }).map(function (m) { return ({
                key: formatPeriod(AnalyticPeriodsEnum.MONTH, m, endOfMonth(m)),
                from: m,
                to: endOfMonth(m)
            }); });
        default:
            return [];
    }
}
export var formatPeriod = function (interval, currentDate, nextDate) {
    switch (interval) {
        case AnalyticPeriodsEnum.HOUR:
            return format(currentDate, 'MMM d, HH:00');
        case AnalyticPeriodsEnum.MONTH:
            return format(currentDate, 'MMM yyyy');
        case AnalyticPeriodsEnum.DAY:
            return format(currentDate, 'MMM d');
        default:
            return "".concat(format(currentDate, 'MMM d'), " - ").concat(format(nextDate, 'MMM d'));
    }
};
export var getInterval = function (dateFrom, dateTo) {
    var parsedDateFrom = startOfDay(new Date(dateFrom));
    var parsedDateTo = endOfDay(new Date(dateTo));
    var daysDifference = dateFrom instanceof Date && dateTo instanceof Date
        ? differenceInDays(dateTo, dateFrom)
        : differenceInDays(parsedDateTo, parsedDateFrom);
    if (daysDifference === 0)
        return AnalyticPeriodsEnum.HOUR;
    if (daysDifference <= 20)
        return AnalyticPeriodsEnum.DAY;
    if (daysDifference <= 93)
        return AnalyticPeriodsEnum.WEEK;
    return AnalyticPeriodsEnum.MONTH;
};
/** SQL bucket size for analytic queries. WEEK charts still aggregate by DAY in SQL. */
export var getSqlGroupBy = function (interval) {
    if (interval === AnalyticPeriodsEnum.HOUR) {
        return AnalyticPeriodsEnum.HOUR;
    }
    if (interval === AnalyticPeriodsEnum.MONTH) {
        return AnalyticPeriodsEnum.MONTH;
    }
    return AnalyticPeriodsEnum.DAY;
};
// eslint-disable-next-line max-statements
export function groupGmailAnalyticByPeriod(analytics, dateFrom, dateTo) {
    var interval = getInterval(dateFrom, dateTo);
    var results = [];
    var currentDate = dateFrom;
    if (interval === AnalyticPeriodsEnum.WEEK) {
        var weekPeriods = getWeekPeriods(dateFrom, dateTo);
        weekPeriods.forEach(function (period) {
            var filtered = analytics.filter(function (activity) {
                var date = new Date(activity.date);
                return date >= period.dateFrom && date <= period.dateTo;
            });
            var _a = filtered.reduce(function (acc, a) { return ({
                emailsReceivedCount: acc.emailsReceivedCount + a.emailsReceivedCount,
                emailsSentCount: acc.emailsSentCount + a.emailsSentCount
            }); }, { emailsReceivedCount: 0, emailsSentCount: 0 }), emailsReceivedCount = _a.emailsReceivedCount, emailsSentCount = _a.emailsSentCount;
            results.push({
                date: period.period,
                emailsReceivedCount: emailsReceivedCount,
                emailsSentCount: emailsSentCount
            });
        });
    }
    else {
        if (interval === AnalyticPeriodsEnum.MONTH) {
            currentDate = startOfMonth(dateFrom);
        }
        var _loop_1 = function () {
            var isMonth = interval === AnalyticPeriodsEnum.MONTH;
            var periodStart = isMonth ? startOfMonth(currentDate) : currentDate;
            var periodEnd = void 0;
            var nextDate = isMonth
                ? addMonths(currentDate, 1)
                : interval === AnalyticPeriodsEnum.DAY
                    ? addDays(currentDate, 1)
                    : addWeeks(currentDate, 1);
            if (isMonth) {
                periodEnd = endOfMonth(currentDate);
                if (periodEnd > dateTo)
                    periodEnd = dateTo;
                if (periodStart < dateFrom) {
                    currentDate = nextDate;
                    return "continue";
                }
            }
            else {
                periodEnd = nextDate;
            }
            var formattedStart = new Date(periodStart.toDateString());
            var formattedEnd = new Date(periodEnd.toDateString());
            var formattedNextDate = new Date(nextDate.toDateString());
            var formattedDateFrom = new Date(dateFrom.toDateString());
            var formattedDateTo = new Date(dateTo.toDateString());
            var filtered = analytics.filter(function (activity) {
                var date = new Date(activity.date);
                if (date < formattedDateFrom || date > formattedDateTo) {
                    return false;
                }
                if (isMonth) {
                    return date >= formattedStart && date <= formattedEnd;
                }
                return date >= formattedStart && date < formattedNextDate;
            });
            var _a = filtered.reduce(function (acc, a) { return ({
                emailsReceivedCount: acc.emailsReceivedCount + a.emailsReceivedCount,
                emailsSentCount: acc.emailsSentCount + a.emailsSentCount
            }); }, { emailsReceivedCount: 0, emailsSentCount: 0 }), emailsReceivedCount = _a.emailsReceivedCount, emailsSentCount = _a.emailsSentCount;
            var formatPeriodInterval = interval === AnalyticPeriodsEnum.HOUR
                ? AnalyticPeriodsEnum.DAY
                : interval;
            results.push({
                date: formatPeriod(formatPeriodInterval, formattedStart, isMonth ? formattedEnd : nextDate),
                emailsReceivedCount: emailsReceivedCount,
                emailsSentCount: emailsSentCount
            });
            currentDate = nextDate;
        };
        while (currentDate <= dateTo) {
            _loop_1();
        }
    }
    return results;
}
export function groupDriveAnalyticByPeriod(analytics, dateFrom, dateTo) {
    var interval = getInterval(dateFrom, dateTo);
    var results = [];
    var currentDate = dateFrom;
    if (interval === AnalyticPeriodsEnum.WEEK) {
        var weekPeriods = getWeekPeriods(dateFrom, dateTo);
        weekPeriods.forEach(function (period) {
            var docsCreated = analytics
                .filter(function (activity) {
                var date = new Date(activity.date);
                return date >= period.dateFrom && date <= period.dateTo;
            })
                .reduce(function (sum, activity) { return sum + activity.docsCreated; }, 0);
            var docsUpdated = analytics
                .filter(function (activity) {
                var date = new Date(activity.date);
                return date >= period.dateFrom && date <= period.dateTo;
            })
                .reduce(function (sum, activity) { return sum + activity.docsUpdated; }, 0);
            results.push({
                date: period.period,
                docsCreated: docsCreated,
                docsUpdated: docsUpdated
            });
        });
    }
    else {
        var _loop_2 = function () {
            var nextDate = interval === AnalyticPeriodsEnum.DAY
                ? addDays(currentDate, 1)
                : interval === AnalyticPeriodsEnum.MONTH
                    ? addMonths(currentDate, 1)
                    : addWeeks(currentDate, 1);
            var formattedCurrentDate = new Date(currentDate.toDateString());
            var formattedNextDate = new Date(nextDate.toDateString());
            var docsCreated = analytics
                .filter(function (activity) {
                var date = new Date(activity.date);
                return date >= formattedCurrentDate && date < formattedNextDate;
            })
                .reduce(function (sum, activity) { return sum + activity.docsCreated; }, 0);
            var docsUpdated = analytics
                .filter(function (activity) {
                var date = new Date(activity.date);
                return date >= formattedCurrentDate && date < formattedNextDate;
            })
                .reduce(function (sum, activity) { return sum + activity.docsUpdated; }, 0);
            var formattedDate = formatPeriod(interval, formattedCurrentDate, formattedNextDate);
            results.push({
                date: formattedDate,
                docsCreated: docsCreated,
                docsUpdated: docsUpdated
            });
            currentDate = nextDate;
        };
        while (currentDate <= dateTo) {
            _loop_2();
        }
    }
    return results;
}
export var formatPercentageOneDecimal = function (value) {
    return "".concat(Math.round(value * 10) / 10, "%");
};
var formatPercentagePointDiffString = function (delta) {
    if (delta === 0) {
        return '0 pp';
    }
    var sign = delta > 0 ? '+' : '';
    return "".concat(sign).concat(delta, " pp");
};
/**
 * Difference between two percentage metrics in percentage points (pp), not relative %.
 * Use for shares, rates, and other values already expressed as percentages.
 */
export var getPercentagePointDiff = function (previousPct, currentPct, options) {
    var _a, _b, _c;
    var previous = Number.isFinite(previousPct) ? previousPct : 0;
    var current = Number.isFinite(currentPct) ? currentPct : 0;
    var delta = Math.round((current - previous) * 10) / 10;
    return {
        diff: delta,
        diffString: formatPercentagePointDiffString(delta),
        deltaUnit: diffDeltaUnit.percentagePoints,
        comparisonValue: (_a = options === null || options === void 0 ? void 0 : options.comparisonValue) !== null && _a !== void 0 ? _a : previous,
        comparisonValueDisplay: (_b = options === null || options === void 0 ? void 0 : options.comparisonValueDisplay) !== null && _b !== void 0 ? _b : formatPercentageOneDecimal(previous),
        comparisonType: (_c = options === null || options === void 0 ? void 0 : options.comparisonType) !== null && _c !== void 0 ? _c : diffComparisonType.previousPeriod
    };
};
export var getPercentageDiff = function (previousData, currentData, options) {
    var previous = Number.isFinite(previousData) ? previousData : 0;
    var current = Number.isFinite(currentData) ? currentData : 0;
    var base = __assign(__assign({ comparisonValue: previous }, ((options === null || options === void 0 ? void 0 : options.comparisonValueFormat)
        ? { comparisonValueFormat: options.comparisonValueFormat }
        : {})), ((options === null || options === void 0 ? void 0 : options.comparisonType)
        ? { comparisonType: options.comparisonType }
        : {}));
    if (current === 0 && previous === 0) {
        return __assign({ diffString: '0%', diff: 0, deltaUnit: diffDeltaUnit.relativePercent }, base);
    }
    if (previous === 0) {
        var diff_1 = 100;
        var diffString_1 = "+".concat(diff_1, "%");
        return __assign({ diffString: diffString_1, diff: diff_1, deltaUnit: diffDeltaUnit.relativePercent }, base);
    }
    var diff = Math.round(((current - previous) / previous) * 100);
    var diffString = diff > 0 ? "+".concat(diff, "%") : "".concat(diff, "%");
    return __assign({ diffString: diffString, diff: diff, deltaUnit: diffDeltaUnit.relativePercent }, base);
};
export function groupByHour(data, dateFrom, dateTo) {
    var hours = eachHourOfInterval({ start: dateFrom, end: dateTo });
    return hours.map(function (hour) {
        var hourStart = hour.toISOString();
        var formattedHour = format(hour, 'h a');
        var items = data
            .filter(function (item) { return parseISO(item.date).toISOString() === hourStart; })
            .map(function (item) { return item.items; })
            .flat();
        return {
            date: formattedHour,
            items: items
        };
    });
}
export function groupByDay(data, dateFrom, dateTo) {
    var days = eachDayOfInterval({ start: dateFrom, end: dateTo });
    return days.map(function (day) {
        var date = formatISO(day, { representation: 'date' });
        var items = data
            .filter(function (item) { return item.date === date; })
            .map(function (item) { return item.items; })
            .flat();
        return {
            date: date,
            items: items
        };
    });
}
export function groupByWeek(data, dateFrom, dateTo) {
    var dataWeekPeriods = getWeekPeriods(dateFrom, dateTo);
    return dataWeekPeriods.map(function (d) {
        var items = data
            .filter(function (item) {
            var date = new Date(item.date);
            return date >= d.dateFrom && date <= d.dateTo;
        })
            .map(function (item) { return item.items; })
            .flat();
        return {
            date: formatPeriod(AnalyticPeriodsEnum.WEEK, d.dateFrom, d.dateTo),
            items: items
        };
    });
}
export function groupByMonth(data, dateFrom, dateTo) {
    var months = eachMonthOfInterval({ start: dateFrom, end: dateTo });
    return months.map(function (monthStart) {
        var monthEnd = endOfMonth(monthStart);
        var items = data
            .filter(function (item) {
            var date = new Date(item.date);
            return date >= monthStart && date <= monthEnd;
        })
            .map(function (item) { return item.items; })
            .flat();
        return {
            date: formatPeriod(AnalyticPeriodsEnum.MONTH, monthStart, monthEnd),
            items: items
        };
    });
}
export function groupByPeriod(data, dateFrom, dateTo, groupBy) {
    switch (groupBy) {
        case AnalyticPeriodsEnum.HOUR:
            return groupByHour(data, dateFrom, dateTo);
        case AnalyticPeriodsEnum.DAY:
            return groupByDay(data, dateFrom, dateTo);
        case AnalyticPeriodsEnum.WEEK:
            return groupByWeek(data, dateFrom, dateTo);
        case AnalyticPeriodsEnum.MONTH:
            return groupByMonth(data, dateFrom, dateTo);
        default:
            return [];
    }
}
export function getPeriodDateBoundsForInterval(interval, dateFrom, dateTo) {
    switch (interval) {
        case AnalyticPeriodsEnum.DAY:
            return eachDayOfInterval({ start: dateFrom, end: dateTo }).map(function (d) {
                var iso = format(d, 'yyyy-MM-dd');
                return { periodDateFrom: iso, periodDateTo: iso };
            });
        case AnalyticPeriodsEnum.WEEK:
            return getWeekPeriods(dateFrom, dateTo).map(function (w) { return ({
                periodDateFrom: format(w.dateFrom, 'yyyy-MM-dd'),
                periodDateTo: format(w.dateTo, 'yyyy-MM-dd')
            }); });
        case AnalyticPeriodsEnum.MONTH:
            return eachMonthOfInterval({ start: dateFrom, end: dateTo }).map(function (monthStart) {
                var monthEnd = endOfMonth(monthStart);
                return {
                    periodDateFrom: format(monthStart, 'yyyy-MM-dd'),
                    periodDateTo: format(monthEnd, 'yyyy-MM-dd')
                };
            });
        case AnalyticPeriodsEnum.HOUR:
            return eachHourOfInterval({ start: dateFrom, end: dateTo }).map(function (h) {
                var iso = format(h, 'yyyy-MM-dd');
                return { periodDateFrom: iso, periodDateTo: iso };
            });
        default:
            return [];
    }
}
export var getAlertTypesByApplication = function (applicationName) {
    switch (applicationName) {
        case ApplicationEnum.GMAIL:
            return [
                ActivityAlertTypeEnum.USER_SENT_MORE_THAN_X_EMAILS_PER_DAY,
                ActivityAlertTypeEnum.USER_SENT_LESS_THAN_X_EMAILS_PER_DAY
            ];
        case ApplicationEnum.DRIVE:
            return [ActivityAlertTypeEnum.USER_CREATED_OR_EDITED_LESS_THAN_X_PER_DAY];
        case ApplicationEnum.MEET:
            return [
                ActivityAlertTypeEnum.USER_SKIPPED_THE_RECURRING_MEETING,
                ActivityAlertTypeEnum.USER_HOSTED_MEETING_X_MINS_THAN_SCHEDULED
            ];
        default:
            return [];
    }
};
export var formatDuration = function (value, type) {
    if (type === void 0) { type = 'hours'; }
    var totalMinutes = Math.round(type === 'seconds' ? value / 60 : value * 60);
    var hours = Math.floor(totalMinutes / 60);
    var minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0)
        return "".concat(hours, "h ").concat(minutes, "min");
    if (hours > 0)
        return "".concat(hours, "h");
    return "".concat(minutes, "min");
};
export var toReplyRatePercent = function (part, whole) {
    return whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0;
};
export var formatReplyRateDisplay = function (count, rate) {
    return "".concat(count, " / ").concat(rate.toFixed(1), "%");
};
export var formatDiffComparisonValue = function (diffValue) {
    if (diffValue.comparisonValueDisplay !== undefined) {
        return diffValue.comparisonValueDisplay;
    }
    var raw = diffValue.comparisonValue;
    if (raw === undefined) {
        return undefined;
    }
    switch (diffValue.comparisonValueFormat) {
        case 'durationSeconds':
            return formatDuration(raw, 'seconds');
        case 'durationHours':
            return formatDuration(raw, 'hours');
        case 'durationMinutes':
            return formatDuration(raw / 60, 'hours');
        default:
            return Number.isFinite(raw) ? Math.round(raw) : raw;
    }
};
export var buildMetricTotal = function (previous, current, comparisonValueFormat) { return ({
    current: current,
    previous: getPercentageDiff(previous, current, comparisonValueFormat ? { comparisonValueFormat: comparisonValueFormat } : undefined)
}); };
export var randomInt = function (min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};
export function roundToTwoDecimals(value) {
    return Math.round(value * 100) / 100;
}
export var chatSpaceRoomTypes = [
    ChatRoomTypeEnum.GROUP_DIRECT_MESSAGE,
    ChatRoomTypeEnum.SPACE,
    ChatRoomTypeEnum.ROOM,
    ChatRoomTypeEnum.THREAD
];
export var isChatSpaceRoomType = function (roomType) {
    return chatSpaceRoomTypes.includes(roomType);
};
export var chatOneToOneRoomTypes = [
    ChatRoomTypeEnum.GROUP_DIRECT_MESSAGE,
    ChatRoomTypeEnum.USER_TO_USER_DIRECT_MESSAGE,
    ChatRoomTypeEnum.PRIVATE,
    ChatRoomTypeEnum.USER_TO_APP_DIRECT_MESSAGE,
    ChatRoomTypeEnum.UNKNOWN
];
export var getPeriodInTimeZone = function (dateFrom, dateTo, timeZone) {
    var dateFromToUTC = fromZonedTime(dateFrom, timeZone).toISOString();
    var dateToToUTC = fromZonedTime(endOfDay(new Date(dateTo)), timeZone).toISOString();
    return {
        dateFrom: dateFromToUTC,
        dateTo: dateToToUTC,
        dateFromStartOfDay: startOfDay(new Date(dateFrom)),
        dateToEndOfDay: endOfDay(new Date(dateTo))
    };
};
export var isDateInRange = function (date, from, to) {
    return date.getTime() >= from.getTime() && date.getTime() <= to.getTime();
};
export var checkIsProcessingOldData = function (dateProcessing, workspaceCreationDate, maxFetchDays, options) {
    if (maxFetchDays === void 0) { maxFetchDays = 180; }
    if (options === null || options === void 0 ? void 0 : options.lastProcessedDate) {
        return dateProcessing > options.lastProcessedDate;
    }
    var maxProcessingDate = subDays(new Date(), maxFetchDays);
    var lastDayProcessing = maxProcessingDate > workspaceCreationDate
        ? maxProcessingDate
        : workspaceCreationDate;
    return dateProcessing >= lastDayProcessing;
};
export var isUserDisabled = function (profileId, email) {
    if (!profileId && !email)
        return false;
    return DISABLED_USERS.some(function (user) { return user.profileId === profileId || user.email === email; });
};
export var getMappedData = function (data, groupByPeriods) {
    return groupByPeriods === AnalyticPeriodsEnum.HOUR
        ? data.map(function (event) { return ({
            items: [event],
            date: formatISO(event.date)
        }); })
        : data.map(function (event) { return ({
            items: [event],
            date: formatISO(event.date, { representation: 'date' })
        }); });
};
export var groupByOrgUnit = function (data, orgUnits, usersInOrgUnits) {
    return orgUnits.map(function (orgUnit) {
        var userIdsInOrgUnit = usersInOrgUnits
            .filter(function (user) { return user.orgUnitId === orgUnit.id; })
            .map(function (user) { return user.userId; });
        var items = data.map(function (item) {
            var eventsCount = item.items
                .filter(function (event) { return userIdsInOrgUnit.includes(event.userId); })
                .reduce(function (acc, event) { return acc + Number(event._count.id); }, 0);
            return {
                date: item.date,
                count: eventsCount
            };
        });
        return {
            id: orgUnit.id,
            title: orgUnit.title,
            activities: items
        };
    });
};
export var groupByUser = function (data, users) {
    return users.map(function (user) {
        var items = data.map(function (item) {
            var eventsCount = item.items
                .filter(function (event) { return event.userId === user.id; })
                .reduce(function (acc, event) { return acc + Number(event._count.id); }, 0);
            return {
                date: item.date,
                count: eventsCount
            };
        });
        return {
            id: user.id,
            title: user.name || user.email,
            activities: items
        };
    });
};
export var activityGroupByOrgUnitOrUser = function (dateFromFilter, dateToFilter, groupBy, orgUnits, users, usersInOrgUnits, data, groupByPeriods) {
    var dataGroupedByPeriod = getMappedData(data, groupByPeriods);
    var eventsGroupedByPeriod = groupByPeriod(dataGroupedByPeriod, dateFromFilter, dateToFilter, groupByPeriods);
    if (groupBy === 'orgUnit') {
        var dataGroupedByOrgUnit = groupByOrgUnit(eventsGroupedByPeriod, orgUnits, usersInOrgUnits);
        return dataGroupedByOrgUnit;
    }
    return groupByUser(eventsGroupedByPeriod, users);
};
export var transformToGroupedChartResponse = function (data, groupBy, orgUnits, users, usersInOrgUnits) {
    if (groupBy === 'orgUnit') {
        var userIdsByOrgUnitId_1 = new Map();
        for (var _i = 0, usersInOrgUnits_1 = usersInOrgUnits; _i < usersInOrgUnits_1.length; _i++) {
            var _a = usersInOrgUnits_1[_i], orgUnitId = _a.orgUnitId, userId = _a.userId;
            if (orgUnitId === null)
                continue;
            var set = userIdsByOrgUnitId_1.get(orgUnitId);
            if (!set) {
                set = new Set();
                userIdsByOrgUnitId_1.set(orgUnitId, set);
            }
            set.add(userId);
        }
        return orgUnits.map(function (orgUnit) {
            var _a;
            var userIds = (_a = userIdsByOrgUnitId_1.get(orgUnit.id)) !== null && _a !== void 0 ? _a : new Set();
            var activities = data.map(function (period) {
                var count = period.users
                    .filter(function (u) { return userIds.has(u.userId); })
                    .reduce(function (sum, u) { return sum + u.value; }, 0);
                return { date: period.date, count: count };
            });
            return { id: orgUnit.id, title: orgUnit.title, activities: activities };
        });
    }
    var valueByUserIdPerPeriod = data.map(function (period) {
        var _a;
        var map = new Map();
        for (var _i = 0, _b = period.users; _i < _b.length; _i++) {
            var u = _b[_i];
            map.set(u.userId, ((_a = map.get(u.userId)) !== null && _a !== void 0 ? _a : 0) + u.value);
        }
        return { date: period.date, valueByUserId: map };
    });
    var result = users.map(function (user) {
        var activities = valueByUserIdPerPeriod.map(function (_a) {
            var _b;
            var date = _a.date, valueByUserId = _a.valueByUserId;
            return ({
                date: date,
                count: (_b = valueByUserId.get(user.id)) !== null && _b !== void 0 ? _b : 0
            });
        });
        return {
            id: user.id,
            title: user.name || user.email,
            activities: activities
        };
    });
    return result.filter(function (group) { return group.activities.some(function (a) { return a.count > 0; }); });
};
/**
 * IANA names browsers may still send but PostgreSQL (newer tzdata) rejects in
 * `AT TIME ZONE` / zone casts. Extend when similar deprecations appear.
 */
var IANA_TIMEZONE_ALIASES_FOR_POSTGRES = {
    'Europe/Kiev': 'Europe/Kyiv'
};
export var normalizeIanaTimeZoneForPostgres = function (timeZone) { var _a; return (_a = IANA_TIMEZONE_ALIASES_FOR_POSTGRES[timeZone]) !== null && _a !== void 0 ? _a : timeZone; };
export var pgConvertTzRaw = function (columnExpr, timeZoneIana) {
    var zone = normalizeIanaTimeZoneForPostgres(timeZoneIana).replace(/'/g, "''");
    return Prisma.sql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["(", "::timestamp AT TIME ZONE 'UTC') AT TIME ZONE ", ""], ["(", "::timestamp AT TIME ZONE 'UTC') AT TIME ZONE ", ""])), Prisma.raw(columnExpr), Prisma.raw("'".concat(zone, "'")));
};
export var getTimeZoneForQuery = function (timeZone) {
    var timeZoneForQuery = formatTz(new Date(), 'xxx', {
        timeZone: normalizeIanaTimeZoneForPostgres(timeZone)
    });
    return timeZoneForQuery;
};
export var getSelectQueryRawFilterByDate = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.timeZone, timeZone = _c === void 0 ? 'UTC' : _c, _d = _b.groupBy, groupBy = _d === void 0 ? AnalyticPeriodsEnum.DAY : _d, _e = _b.dateColumn, dateColumn = _e === void 0 ? 'date' : _e;
    var localTs = pgConvertTzRaw(dateColumn, timeZone);
    switch (groupBy) {
        case AnalyticPeriodsEnum.HOUR:
            return Prisma.sql(templateObject_2 || (templateObject_2 = __makeTemplateObject(["to_char(date_trunc('hour', ", "), 'YYYY-MM-DD HH24:00:00')"], ["to_char(date_trunc('hour', ", "), 'YYYY-MM-DD HH24:00:00')"])), localTs);
        case AnalyticPeriodsEnum.DAY:
        case AnalyticPeriodsEnum.WEEK:
            return Prisma.sql(templateObject_3 || (templateObject_3 = __makeTemplateObject(["(", ")::date"], ["(", ")::date"])), localTs);
        case AnalyticPeriodsEnum.MONTH:
            return Prisma.sql(templateObject_4 || (templateObject_4 = __makeTemplateObject(["to_char(date_trunc('month', ", "), 'YYYY-MM')"], ["to_char(date_trunc('month', ", "), 'YYYY-MM')"])), localTs);
    }
};
export var normalizeAnalyticSqlDateKey = function (value, groupBy) {
    if (groupBy === AnalyticPeriodsEnum.HOUR) {
        var parsed = typeof value === 'string' ? new Date(value.replace(' ', 'T')) : value;
        return formatISO(startOfHour(parsed));
    }
    if (typeof value === 'string') {
        return value.slice(0, 10);
    }
    return formatISO(value, { representation: 'date' });
};
export var getPreviousPeriod = function (dateFrom, dateTo) {
    var diff = differenceInMilliseconds(dateTo, dateFrom);
    var prevPeriodDateTo = subDays(dateFrom, 1);
    var prevPeriodDateFrom = subMilliseconds(prevPeriodDateTo, diff);
    return {
        prevPeriodDateFrom: format(prevPeriodDateFrom, 'yyyy-MM-dd'),
        prevPeriodDateTo: format(prevPeriodDateTo, 'yyyy-MM-dd')
    };
};
export function getPeriodComparisonLabel(item) {
    var _a, _b, _c;
    return (_c = (_b = (_a = item.date) !== null && _a !== void 0 ? _a : item.name) !== null && _b !== void 0 ? _b : item.period) !== null && _c !== void 0 ? _c : '';
}
var WEEK_STARTS_ON_MONDAY = 1;
function getExpectedPreviousPeriodBucketStart(interval, currentBucketFrom, currentSelectionFrom, previousSelectionFrom) {
    if (interval === AnalyticPeriodsEnum.WEEK) {
        var currentAnchor = startOfWeek(currentSelectionFrom, {
            weekStartsOn: WEEK_STARTS_ON_MONDAY
        });
        var previousAnchor = startOfWeek(previousSelectionFrom, {
            weekStartsOn: WEEK_STARTS_ON_MONDAY
        });
        var weekOffset = differenceInWeeks(currentBucketFrom, currentAnchor);
        return addWeeks(previousAnchor, weekOffset);
    }
    if (interval === AnalyticPeriodsEnum.MONTH) {
        var monthOffset = differenceInMonths(startOfMonth(currentBucketFrom), startOfMonth(currentSelectionFrom));
        return addMonths(startOfMonth(previousSelectionFrom), monthOffset);
    }
    var shiftMs = differenceInMilliseconds(currentSelectionFrom, previousSelectionFrom);
    return startOfDay(subMilliseconds(currentBucketFrom, shiftMs));
}
function findPreviousBucketIndex(previousBounds, expectedPrevStart, interval) {
    return previousBounds.findIndex(function (bucket) {
        return interval === AnalyticPeriodsEnum.MONTH
            ? isSameMonth(bucket.from, expectedPrevStart)
            : isSameDay(bucket.from, expectedPrevStart);
    });
}
function getFallbackPreviousBucketIndex(currentIndex, currentLabels, previousLabels) {
    var endAlignedIndex = currentIndex - Math.max(0, currentLabels.length - previousLabels.length);
    if (endAlignedIndex >= 0 && endAlignedIndex < previousLabels.length) {
        return endAlignedIndex;
    }
    return currentIndex < previousLabels.length ? currentIndex : null;
}
function resolvePreviousLabelIndex(previousBucketIndex, previousBounds, previousLabels) {
    var _a;
    var previousKey = (_a = previousBounds[previousBucketIndex]) === null || _a === void 0 ? void 0 : _a.key;
    if (!previousKey) {
        return previousBucketIndex < previousLabels.length
            ? previousBucketIndex
            : null;
    }
    var labelIndex = previousLabels.indexOf(previousKey);
    return labelIndex >= 0 ? labelIndex : previousBucketIndex;
}
export function toPeriodComparisonMatchOptions(dateFrom, dateTo, interval) {
    return {
        dateFrom: typeof dateFrom === 'string' ? dateFrom : format(dateFrom, 'yyyy-MM-dd'),
        dateTo: typeof dateTo === 'string' ? dateTo : format(dateTo, 'yyyy-MM-dd'),
        interval: interval
    };
}
function buildPeriodComparisonContext(_a) {
    var dateFrom = _a.dateFrom, dateTo = _a.dateTo, interval = _a.interval;
    var _b = getPreviousPeriod(dateFrom, dateTo), prevPeriodDateFrom = _b.prevPeriodDateFrom, prevPeriodDateTo = _b.prevPeriodDateTo;
    return {
        currentBounds: buildPeriods(parseISO(dateFrom), parseISO(dateTo), interval),
        previousBounds: buildPeriods(parseISO(prevPeriodDateFrom), parseISO(prevPeriodDateTo), interval),
        currentSelectionFrom: startOfDay(parseISO(dateFrom)),
        previousSelectionFrom: startOfDay(parseISO(prevPeriodDateFrom)),
        interval: interval
    };
}
function resolveMatchingPreviousPeriodIndex(currentIndex, currentLabels, previousLabels, context) {
    var _a, _b;
    if (previousLabels.length === 0 ||
        currentLabels.length === 0 ||
        currentIndex < 0) {
        return null;
    }
    var currentLabel = currentLabels[currentIndex];
    if (!currentLabel) {
        return null;
    }
    var currentBucket = (_a = context.currentBounds.find(function (bucket) { return bucket.key === currentLabel; })) !== null && _a !== void 0 ? _a : context.currentBounds[currentIndex];
    if (!currentBucket) {
        return getFallbackPreviousBucketIndex(currentIndex, currentLabels, previousLabels);
    }
    var expectedPrevStart = getExpectedPreviousPeriodBucketStart(context.interval, currentBucket.from, context.currentSelectionFrom, context.previousSelectionFrom);
    var previousBucketIndex = findPreviousBucketIndex(context.previousBounds, expectedPrevStart, context.interval);
    if (previousBucketIndex < 0) {
        previousBucketIndex =
            (_b = getFallbackPreviousBucketIndex(currentIndex, currentLabels, previousLabels)) !== null && _b !== void 0 ? _b : -1;
    }
    return previousBucketIndex >= 0
        ? resolvePreviousLabelIndex(previousBucketIndex, context.previousBounds, previousLabels)
        : null;
}
export function createPeriodComparisonMatcher(currentLabels, previousLabels, options) {
    var context = buildPeriodComparisonContext(options);
    var getIndex = function (currentIndex) {
        return resolveMatchingPreviousPeriodIndex(currentIndex, currentLabels, previousLabels, context);
    };
    return {
        getIndex: getIndex,
        getItem: function (currentIndex, items) {
            var matchedIndex = getIndex(currentIndex);
            return matchedIndex != null ? items[matchedIndex] : undefined;
        },
        getKey: function (currentPeriodKey) {
            var currentIndex = currentLabels.indexOf(currentPeriodKey);
            if (currentIndex < 0) {
                return undefined;
            }
            var matchedIndex = getIndex(currentIndex);
            return matchedIndex != null ? previousLabels[matchedIndex] : undefined;
        }
    };
}
export function createPeriodComparisonMatcherFromItems(currentItems, previousItems, options) {
    return createPeriodComparisonMatcher(currentItems.map(getPeriodComparisonLabel), previousItems.map(getPeriodComparisonLabel), options);
}
export function getMatchingPreviousPeriodIndex(currentIndex, currentLabels, previousLabels, options) {
    return resolveMatchingPreviousPeriodIndex(currentIndex, currentLabels, previousLabels, buildPeriodComparisonContext(options));
}
export function getMatchingPreviousPeriodItem(currentIndex, currentItems, previousItems, options) {
    var matchedIndex = resolveMatchingPreviousPeriodIndex(currentIndex, currentItems.map(getPeriodComparisonLabel), previousItems.map(getPeriodComparisonLabel), buildPeriodComparisonContext(options));
    return matchedIndex != null ? previousItems[matchedIndex] : undefined;
}
export function getMatchingPreviousPeriodKey(currentPeriodKey, currentPeriodKeys, previousPeriodKeys, options) {
    var currentIndex = currentPeriodKeys.indexOf(currentPeriodKey);
    if (currentIndex < 0) {
        return undefined;
    }
    var matchedIndex = resolveMatchingPreviousPeriodIndex(currentIndex, currentPeriodKeys, previousPeriodKeys, buildPeriodComparisonContext(options));
    return matchedIndex != null ? previousPeriodKeys[matchedIndex] : undefined;
}
export var getCurrentAndPreviousPeriodBounds = function (dateFrom, dateTo, timeZone) {
    var _a = getPeriodInTimeZone(dateFrom, dateTo, timeZone), currentFrom = _a.dateFrom, currentTo = _a.dateTo;
    var _b = getPreviousPeriod(dateFrom, dateTo), prevPeriodDateFrom = _b.prevPeriodDateFrom, prevPeriodDateTo = _b.prevPeriodDateTo;
    var _c = getPeriodInTimeZone(prevPeriodDateFrom, prevPeriodDateTo, timeZone), previousFrom = _c.dateFrom, previousTo = _c.dateTo;
    return {
        dateFrom: new Date(currentFrom),
        dateTo: new Date(currentTo),
        prevDateFrom: new Date(previousFrom),
        prevDateTo: new Date(previousTo)
    };
};
export var calculateAverage = function (total, count) {
    if (count === 0)
        return 0;
    return Math.round(total / count);
};
export var toPercent = function (part, whole) {
    return whole > 0 ? Math.round((part / whole) * 100) : 0;
};
/**
 * The in-app share is derived from the standalone one so the pair always adds up to 100%
 * instead of drifting apart through independent rounding.
 */
export var resolveStandaloneShares = function (actions, standaloneActions) {
    if (actions <= 0) {
        return { standalone: 0, inApp: 0 };
    }
    var standalone = toPercent(standaloneActions, actions);
    return { standalone: standalone, inApp: 100 - standalone };
};
/**
 * Largest-remainder rounding: keeps a set of category shares adding up to exactly 100%,
 * which independent `Math.round` calls cannot guarantee.
 */
export var distributeSharePercentages = function (counts) {
    var total = counts.reduce(function (sum, count) { return sum + count; }, 0);
    if (total <= 0) {
        return counts.map(function () { return 0; });
    }
    var exactShares = counts.map(function (count) { return (count / total) * 100; });
    var shares = exactShares.map(function (share) { return Math.floor(share); });
    var leftover = 100 - shares.reduce(function (sum, share) { return sum + share; }, 0);
    exactShares
        .map(function (share, index) { return ({ index: index, remainder: share - Math.floor(share) }); })
        .sort(function (a, b) { return b.remainder - a.remainder; })
        .slice(0, Math.max(leftover, 0))
        .forEach(function (_a) {
        var index = _a.index;
        shares[index] += 1;
    });
    return shares;
};
export var getTeamMemberIds = function (orgUnitUserIds, selectedUserId) {
    return selectedUserId !== null
        ? orgUnitUserIds.filter(function (id) { return id !== selectedUserId; })
        : orgUnitUserIds;
};
export var getTeamMemberCount = function (orgUnitUserIds, selectedUserId) { return getTeamMemberIds(orgUnitUserIds, selectedUserId).length; };
export var calculateTeamAverage = function (teamTotal, orgUnitUserIds, selectedUserId) {
    return calculateAverage(teamTotal, getTeamMemberCount(orgUnitUserIds, selectedUserId));
};
export var averageTime = function (activities, type, timeZoneIana) {
    var validActivities = activities.filter(Boolean);
    if (!validActivities.length)
        return null;
    var totalMinutes = validActivities.reduce(function (sum, item) {
        var date = type === 'start' ? item.firstActivity : item.lastActivity;
        if (timeZoneIana) {
            var wallClock = formatInTimeZone(date, timeZoneIana, 'HH mm');
            var _a = wallClock.split(' ').map(function (n) { return parseInt(n, 10); }), h = _a[0], m = _a[1];
            return sum + h * 60 + m;
        }
        return sum + date.getHours() * 60 + date.getMinutes();
    }, 0);
    var avg = totalMinutes / validActivities.length;
    var roundedTotalMinutes = Math.round(avg);
    var hours = Math.floor(roundedTotalMinutes / 60);
    var minutes = roundedTotalMinutes % 60;
    return "".concat(hours.toString().padStart(2, '0'), ":").concat(minutes
        .toString()
        .padStart(2, '0'), ":00");
};
export var sumBy = function (items, key) {
    return items.reduce(function (sum, item) { return sum + item[key]; }, 0);
};
export var formatGooglePhotoToDataUrl = function (photoData, mimeType) {
    if (mimeType === void 0) { mimeType = 'image/jpeg'; }
    var base64 = photoData.replace(/-/g, '+').replace(/_/g, '/');
    return "data:".concat(mimeType, ";base64,").concat(base64);
};
export function uncategorizedLast(rows, isUncategorized) {
    var categorized = rows.filter(function (r) { return !isUncategorized(r); });
    var uncategorizedRows = rows.filter(function (r) { return isUncategorized(r); });
    return __spreadArray(__spreadArray([], categorized, true), uncategorizedRows, true);
}
export function sliceRowsKeepingUncategorizedLast(orderedRows, displayedCount, isUncategorized) {
    var uncategorizedRow = orderedRows.find(isUncategorized);
    var categorized = orderedRows.filter(function (r) { return !isUncategorized(r); });
    var displayedRows = uncategorizedRow
        ? __spreadArray(__spreadArray([], categorized.slice(0, Math.max(0, displayedCount - 1)), true), [
            uncategorizedRow
        ], false) : orderedRows.slice(0, displayedCount);
    var hasMore = uncategorizedRow
        ? categorized.length > displayedCount - 1
        : displayedCount < orderedRows.length;
    return { displayedRows: displayedRows, hasMore: hasMore };
}
export var buildDashboardDeepLink = function (_a) {
    var appBaseUrl = _a.appBaseUrl, dateFrom = _a.dateFrom, dateTo = _a.dateTo, orgUnitId = _a.orgUnitId, userId = _a.userId;
    var base = appBaseUrl.replace(/\/$/, '');
    var query = new URLSearchParams();
    query.set('period', 'custom');
    query.set('datefrom', dateFrom);
    query.set('dateto', dateTo);
    if (orgUnitId != null) {
        query.set('orgunit', String(orgUnitId));
    }
    if (userId != null) {
        query.set('user', String(userId));
    }
    return "".concat(base, "/dashboard?").concat(query.toString());
};
export { sharePercent } from '@workalizer/shared/utils/meetingsAnalytics';
export { averageDecimalMemberValues, averageMemberNumericFields, averageSummedMemberValues, groupRowsByMemberId } from '@workalizer/shared/utils/teamAverageUtils';
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
